import { LightbulbLit, LogoText2 } from '@/resources/assets/images';
import Button from '@/components/Button/newButtons';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { useState } from 'react';
import { RootState } from '@/rootReducer';
import { useSelector } from 'react-redux';
import { sendAnalytics } from '@/services/analyticsService';
import { useOnboardingContext } from '../context/OnboardingContext';

export default function OnboardingOutreach() {
	const { onboardingData, setOnboardingData, nextPage } =
		useOnboardingContext();
	const [targetAudienceError, setTargetAudienceError] = useState<
		string | null
	>(null);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const handleNextClick = () => {
		const length = onboardingData.targetAudience?.length ?? 0;

		if (length < 1) {
			setTargetAudienceError(
				'Please fill in the target audience before continuing.'
			);
			return;
		}

		if (length > 1000) {
			setTargetAudienceError(
				'Target audience must be under 1000 characters.'
			);
			return;
		}

		setTargetAudienceError(null);
		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'Onboarding Flow - Submitted audience',
			properties: {
				page: 'Step 1: Outreach Page',
				targetAudience: onboardingData.targetAudience
			}
		});
		nextPage();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleNextClick();
		}
	};

	const variant =
		user?.features?.onboardingTestGroup === 'test'
			? 'OnboardingFlowA'
			: 'OnboardingFlowB';

	const getCopy = () => {
		if (variant === 'OnboardingFlowA') {
			return {
				title: "Let's find your next customer",
				body: (
					<>
						<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
							Resquared finds and contacts the right businesses
							for you — no lists, no scraping, no guesswork.
							<br />
							Tell us{' '}
							<span className="font-bold text-dm-gold">
								who you&apos;re trying to reach
							</span>
							, and we&apos;ll take it from here.
						</span>
					</>
				)
			};
		}

		// Variant B (default)
		return {
			title: 'Who are you selling to?',
			body: (
				<>
					<span className="font-barlow text-base font-normal leading-8 text-dm-white sm:leading-8">
						We&apos;ll help you pick your ideal customers, write a
						campaign, and send emails from our trusted
						infrastructure.
						<br />
						Just tell us{' '}
						<span className="font-bold text-dm-gold">
							who you&apos;re targeting
						</span>{' '}
						— we&apos;ll find the right business for you.
					</span>
				</>
			)
		};
	};

	const copy = getCopy();

	return (
		<div className="h-full w-full overflow-hidden">
			<div className="h-[3.75rem] w-full overflow-hidden rounded-tl-lg rounded-tr-lg border-b border-t border-dm-gray px-4 sm:h-[6.375rem] sm:px-20 lg:rounded-tr-none lg:border-l">
				<div className="flex h-full w-full items-center">
					<div className="mx-auto flex w-full max-w-[79rem] items-center justify-between">
						<div className="flex items-center gap-4">
							<LightbulbLit className="h-[2rem] w-[1.375rem] shrink-0" />
							<h1 className="font-barlow text-lg font-semibold leading-[1.375rem] text-dm-white">
								Step 1 of 4: Your Audience
							</h1>
						</div>
						<LogoText2 className="h-6 w-[6.5625rem] transition-all duration-300 3xl:-mr-[2.5rem]" />
					</div>
				</div>
			</div>

			{/* Main Content  */}
			<div className="h-full border-dm-gray px-4 py-6 sm:py-10 sm:pl-12 sm:pr-16 lg:border-l">
				<div className="mx-auto flex min-h-[25.188rem] w-full max-w-[79rem] flex-col overflow-hidden rounded-2xl border border-dm-gray bg-dm-black3/50 px-4 pt-8 sm:px-20">
					<div className="flex flex-1 flex-col gap-10 overflow-auto pb-8 sm:overflow-visible sm:pb-0">
						<div className="flex flex-col gap-4">
							{/* Step Title */}
							<span className="font-barlow text-xl font-semibold leading-8 tracking-normal text-dm-white sm:text-2xl sm:leading-10">
								{copy.title}
							</span>

							{/* Body Text */}
							<div className="max-w-[41rem] text-base">
								{copy.body}
							</div>
							<div className="flex flex-col gap-1">
								{/* Input */}
								<input
									autoFocus
									type="text"
									placeholder="E.g. Hair salons in Brooklyn"
									className="w-full max-w-[31.5rem] rounded border border-gray-300 bg-white p-3 font-barlow text-sm font-normal leading-7 text-black focus:border-blue-500 focus:outline-none sm:text-base sm:leading-8"
									value={onboardingData.targetAudience}
									onKeyDown={handleKeyDown}
									onChange={e =>
										setOnboardingData({
											targetAudience: e.target.value
										})
									}
								/>
								<div className="-mt-1 h-[1rem]">
									{targetAudienceError && (
										<ErrorMessage
											message={targetAudienceError}
										/>
									)}
								</div>
							</div>
						</div>

						{/* Button */}
						<div className="flex flex-col justify-start gap-1">
							<Button
								type="button"
								smart
								variant="primary"
								className="rounded-lg px-6 text-white"
								onClick={handleNextClick}
							>
								Next: Your Offer
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
